import React, { ChangeEvent, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { Dayjs } from 'dayjs';
import orderBy from 'lodash/orderBy';
import styled from 'styled-components';
import { Button, Checkbox, HR, Input, Label, Message, Select, SidebarButton, SidebarHeaderTitle, SidebarTitle, TooltipIcon, TooltipWrapper } from 'components';
import { ISelectOption } from 'components/Select/types';
import { appConfig } from 'config/app';
import { theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { ISideBarError } from 'screens/MakePayment/Calculator/types';
import { SidebarMobileActions } from 'screens/MakePayment/components';
import { selectAPIRequestErrors } from 'store/ApiClient';
import { Payment } from 'store/Payment';
import { IStore } from 'store/types';
import { IApiErrorPayloadWithCode, ITipCalculatorParticipant } from 'types';
import { centsToDollarsDisplay, centsToNegativeDollarsDisplay } from 'utils/currency';

interface ISummaryProps {
  authorizedAgreement?: boolean;
  cappedWarning?: ISideBarError;
  unallocatedFundsWarning?: ISideBarError;
  dataCy?: string;
  description?: string;
  paymentCategory?: string;
  paymentCategories?: string[];
  error?: ISideBarError;
  isLoading?: boolean;
  isProcessing?: boolean;
  onAgreementChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onDescriptionChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onPaymentCategoryChange?: (value: string) => void;
  onExpand?: () => void;
  onSecondaryAction?: () => void;
  onPrimaryAction?: () => void;
  participants?: ITipCalculatorParticipant[];
  paymentDate: Dayjs;
  preventSubmit?: boolean;
  totalHoldbacks: number;
  totalInstantTips: number;
  totalPayrollTips: number;
  totalTips: number;
  secondaryActionType?: 'cancel' | 'back';
  sidebarOpen?: boolean;
}

const StyledMessage = styled(Message)`
  margin-bottom: ${toRem(16)};

  > &:not(:last-child) {
    margin-bottom: ${toRem(20)};
  }
`;

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  flex-grow: 1;
  width: 100%;
  min-height: 100%;
  padding: ${toRem(16)} ${toRem(40)};

  @media (min-width: ${theme.breakpoint.large}px) {
    padding: ${toRem(40)};
  }
`;

const MobileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-bottom: ${toRem(24)};

  @media (min-width: ${theme.breakpoint.large}px) {
    display: none;
  }
`;

const SummaryContent = styled.div<{ isOpen?: boolean }>`
  /* hide collapsed content in mobile-view */
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  flex-flow: column nowrap;

  @media (min-width: ${theme.breakpoint.large}px) {
    display: flex;
  }
`;

const Column = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin-bottom: ${toRem(20)};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${toRem(8)};
`;

const Title = styled.p`
  font-size: ${toRem(24)};
  font-weight: ${theme.font.weight.semiBold};
`;

const SubTitle = styled.p`
  font-size: ${toRem(14)};
`;

const LineItem = styled.p`
  font-size: ${toRem(14)};
  font-weight: ${theme.font.weight.regular};
  color: ${theme.color.neutral[500]};
`;

const PaymentDetails = styled(Column)``;

const FieldContainer = styled(Column)`
  margin-top: ${toRem(24)};

  label {
    margin-bottom: ${toRem(8)};

    span {
      color: ${theme.color.neutral[400]};
    }
  }
`;

const PaymentCategoryContainer = styled(FieldContainer)`
  margin-bottom: 0;

  input {
    &::placeholder {
      color: ${theme.color.neutral[400]} !important;
    }
  }
`;

const StyledButton = styled(Button)`
  text-transform: capitalize;
`;

const TermsFooter = styled(Column)`
  justify-content: flex-end;
  margin: 0;

  @media (min-width: ${theme.breakpoint.large}px) {
    flex-grow: 1;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${toRem(24)};
  width: 100%;

  @media (max-width: ${theme.breakpoint.large}px) {
    padding-bottom: ${toRem(12)};
  }

  button {
    flex-grow: 1;
    text-transform: capitalize;

    &:first-child {
      margin-right: ${toRem(16)};
    }
  }
`;

const Summary = ({
  authorizedAgreement,
  dataCy = 'tip-calc-review-summary',
  description,
  cappedWarning,
  unallocatedFundsWarning,
  paymentCategory,
  paymentCategories,
  error,
  isLoading,
  isProcessing,
  onAgreementChange,
  onPaymentCategoryChange,
  onSecondaryAction,
  onDescriptionChange,
  onExpand,
  onPrimaryAction,
  participants,
  paymentDate,
  preventSubmit,
  totalHoldbacks,
  totalInstantTips,
  totalPayrollTips,
  totalTips,
  secondaryActionType = 'cancel',
  sidebarOpen,
}: ISummaryProps) => {
  const apiSubmitErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Payment.CREATE_PAYMENT_REQUEST)) as IApiErrorPayloadWithCode;

  const disableSubmittingPayment = useMemo(
    () => isProcessing || !authorizedAgreement || (paymentCategories?.length > 0 && !paymentCategory) || preventSubmit || apiSubmitErrors?.errorMessages?.length > 0 || error?.preventSubmit,
    [isProcessing, authorizedAgreement, paymentCategories, paymentCategory, preventSubmit, apiSubmitErrors, error]
  );

  const paymentCategoryOptions = useMemo(
    () =>
      orderBy(
        paymentCategories?.map((category) => ({
          value: category,
          label: category,
        })),
        [(cat) => cat.label.toLowerCase()],
        ['asc']
      ),
    [paymentCategories]
  );

  const renderApiErrors = () =>
    apiSubmitErrors?.errorMessages?.map((error: string, index: number) => {
      let err = error;
      const regex = /net \d+ net/gm;
      const parsedMessage = regex.exec(error);

      /**
       * parses net exceeds max tip amount error received from api
       * that comes back in format: "net 240023 net amount..."
       */
      if (parsedMessage !== null) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/naming-convention
        const [_, cents] = parsedMessage[0].split(' ');
        const amount = centsToDollarsDisplay(parseInt(cents, 10));
        err = err.replaceAll(regex, `${amount} net`);
      }

      if (err === 'payment_date already been paid') {
        return <StyledMessage dataCy={`${dataCy}-sidebar-submit-error`} key={`error-message-${index}`} title="Already Paid" message="You have already issued tips for this day." appearance={Appearance.WARNING} />;
      }

      return <StyledMessage dataCy={`${dataCy}-sidebar-submit-error`} key={`error-message-${index}`} title="Payment Error" message={err} appearance={Appearance.WARNING} />;
    });

  return (
    <Layout data-cy={dataCy}>
      <MobileHeader>
        <SidebarHeaderTitle>
          <SidebarButton onClick={onExpand} isOpen={sidebarOpen} />
          <SidebarTitle title="Review Summary" />
        </SidebarHeaderTitle>
        <SidebarMobileActions columns={2}>
          <StyledButton label={secondaryActionType} appearance="white" size="small" disabled={isProcessing} onClick={onSecondaryAction} data-cy={`tip-calc-${secondaryActionType}-btn`} />
          <Button type="submit" label="Submit" appearance="primary" size="small" data-cy="tip-calc-submit-btn" disabled={disableSubmittingPayment} isProcessing={isProcessing} onClick={onPrimaryAction} />
        </SidebarMobileActions>
      </MobileHeader>
      <SummaryContent isOpen={sidebarOpen}>
        <Column>
          <Row data-cy={`${dataCy}-pay-now`}>
            <Title>Pay Now:</Title>
            {isLoading ? <Skeleton width={50} height={20} /> : <Title>{totalInstantTips > 0 ? centsToDollarsDisplay(totalInstantTips) : '$0.00'}</Title>}
          </Row>
          <Row data-cy={`${dataCy}-business-date`}>
            <SubTitle>Business Date:</SubTitle>
            <SubTitle>{isLoading ? <Skeleton width={100} /> : paymentDate?.format('MMMM Do, YYYY')}</SubTitle>
          </Row>
        </Column>
        <PaymentDetails>
          <Row data-cy={`${dataCy}-payment-details`}>
            <SubTitle>Payment Details</SubTitle>
          </Row>
          <Row data-cy={`${dataCy}-amount-entered`}>
            <LineItem>Amount Entered (Gross)</LineItem>
            {isLoading ? <Skeleton width={50} /> : <LineItem>{totalTips > 0 ? centsToDollarsDisplay(totalTips) : '$0.00'}</LineItem>}
          </Row>
          <Row data-cy={`${dataCy}-holdbacks`}>
            <TooltipWrapper>
              <LineItem>Holdbacks</LineItem>
              <TooltipIcon id="holdback">Amount withheld to cover expenses</TooltipIcon>
            </TooltipWrapper>
            {isLoading ? <Skeleton width={50} /> : <LineItem>{totalHoldbacks > 0 ? centsToNegativeDollarsDisplay(totalHoldbacks) : '$0.00'}</LineItem>}
          </Row>
          <Row data-cy={`${dataCy}-instant`}>
            <LineItem>Instant</LineItem>
            {isLoading ? <Skeleton width={50} /> : <LineItem>{totalInstantTips > 0 ? centsToDollarsDisplay(totalInstantTips) : '$0.00'}</LineItem>}
          </Row>
          <Row data-cy={`${dataCy}-payroll`}>
            <LineItem>Payroll</LineItem>
            {isLoading ? <Skeleton width={50} /> : <LineItem>{totalPayrollTips > 0 ? centsToDollarsDisplay(totalPayrollTips) : '$0.00'}</LineItem>}
          </Row>
          {participants?.map((participant) => (
            <Row key={`participant_${participant.name}`} data-cy={`${dataCy}-participant`}>
              <LineItem>{participant.name}</LineItem>
              {isLoading ? <Skeleton width={50} /> : <LineItem>{centsToDollarsDisplay(participant.net)}</LineItem>}
            </Row>
          ))}
        </PaymentDetails>
        <HR />
        {paymentCategories?.length > 0 && (
          <PaymentCategoryContainer data-cy={`${dataCy}-payment-category`}>
            <Label>Category</Label>
            <Select
              data-cy="payment-category-selector"
              values={[paymentCategoryOptions.find((opt) => opt.value === paymentCategory)]}
              options={paymentCategoryOptions}
              onChange={(selected: ISelectOption<string>[]) => onPaymentCategoryChange(selected[0]?.value)}
              placeholder="Please select"
              disabled={isLoading || isProcessing}
              error={authorizedAgreement && !paymentCategory}
              id="payment-category-selector"
            />
          </PaymentCategoryContainer>
        )}
        <FieldContainer data-cy={`${dataCy}-description`}>
          <Label>
            Description <span>(Optional)</span>
          </Label>
          <Input placeholder="Description" data-cy="description-input" defaultValue={description} onBlur={onDescriptionChange} disabled={isLoading || isProcessing} maxLength={100} />
        </FieldContainer>
        {unallocatedFundsWarning && <StyledMessage dataCy={`${dataCy}-sidebar-error`} title={unallocatedFundsWarning.title} message={unallocatedFundsWarning.message} appearance={Appearance.WARNING} />}
        {cappedWarning && <StyledMessage dataCy={`${dataCy}-sidebar-error`} title={cappedWarning.title} message={cappedWarning.message} appearance={Appearance.WARNING} />}
        {error && <StyledMessage dataCy={`${dataCy}-sidebar-error`} title={error.title} message={error.message} appearance={Appearance.WARNING} />}
        {renderApiErrors()}
        <TermsFooter data-cy={`${dataCy}-footer`}>
          <Checkbox
            onChange={onAgreementChange}
            id="authorizedAgreement"
            name="authorizedAgreement"
            data-cy="authorized-agreement-checkbox"
            checked={authorizedAgreement}
            label={
              <span data-cy="authorized-agreement-text">
                I am authorized to make these transactions and aware of the{' '}
                <a href={appConfig.LINKS.TERMS_OF_USE} target="_blank" rel="noopener noreferrer">
                  Terms and Conditions
                </a>{' '}
                to send payments.
              </span>
            }
          />
          <Actions>
            <StyledButton label={secondaryActionType} appearance="white" className="primary-back-btn" size="large" disabled={isProcessing} onClick={onSecondaryAction} data-cy={`tip-calc-${secondaryActionType}-btn`} />
            <Button
              type="submit"
              label="Submit"
              className="primary-submit-btn"
              appearance="primary"
              size="large"
              data-cy="tip-calc-submit-btn"
              data-processing={!!isProcessing}
              disabled={disableSubmittingPayment}
              isProcessing={isProcessing}
              onClick={onPrimaryAction}
            />
          </Actions>
        </TermsFooter>
      </SummaryContent>
    </Layout>
  );
};

export default Summary;
